import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { resolveTeam, Ico, Icons, PlayerAvatar } from './index';
import { tournamentAPI, createMatchSSE } from '../../services/api';
import { formatDateIST } from '../../utils/dateUtils';

export function HomeMatchesPreview() {
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tab, setTab] = useState('live');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const res = await tournamentAPI.getHomeMatches();
      const allMatches = [...(res.data.liveMatches || []), ...(res.data.upcomingMatches || []), ...(res.data.recentMatches || [])];
      const validMatches = allMatches.filter(m => m.player1 && m.player2 && m.player1 !== 'BYE' && m.player2 !== 'BYE');
      setMatches(validMatches);
      setPlayers(res.data.players || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    let interval;
    const es = createMatchSSE(() => loadData());
    es.onerror = () => {
      es.close();
      interval = setInterval(loadData, 30000);
    };
    return () => { es.close(); if (interval) clearInterval(interval); };
  }, []);

  const live = matches.filter(m => m.status === 'live').sort((a, b) => new Date(b.updatedAt || '1970-01-01') - new Date(a.updatedAt || '1970-01-01'));
  const upcoming = matches.filter(m => m.status === 'scheduled' || m.status === 'pending').sort((a, b) => {
    const hasA = a.date && a.time, hasB = b.date && b.time;
    if (hasA && !hasB) return -1;
    if (!hasA && hasB) return 1;
    if (!hasA && !hasB) return 0;
    return new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`);
  });
  const recent = matches.filter(m => m.status === 'completed').sort((a, b) => new Date(b.updatedAt || '1970-01-01') - new Date(a.updatedAt || '1970-01-01'));

  useEffect(() => {
    if (!loading) {
      if (live.length > 0) setTab('live');
      else if (upcoming.length > 0) setTab('upcoming');
      else if (recent.length > 0) setTab('recent');
    }
  }, [loading, live.length, upcoming.length, recent.length]);

  const tabList = [
    { key: 'live', label: 'Live', count: live.length, show: live.length > 0 },
    { key: 'upcoming', label: 'Upcoming', count: upcoming.length, show: upcoming.length > 0 },
    { key: 'recent', label: 'Recent', count: recent.length, show: recent.length > 0 },
  ].filter(t => t.show);

  const currentList = tab === 'live' ? live : tab === 'upcoming' ? upcoming : recent;

  if (!loading && live.length === 0 && upcoming.length === 0 && recent.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 className="heading">LIVE SCORES</h2>
        <Link to="/live" style={{ fontSize: 12, color: '#1E88E5', fontWeight: 600, textDecoration: 'none' }}>View All →</Link>
      </div>

      {tabList.length > 0 && (
        <div className="tabs" style={{ marginBottom: 12 }}>
          {tabList.map(t => (
            <button key={t.key} className={`tab ${tab === t.key ? 'tab-active' : ''}`} onClick={() => setTab(t.key)}>
              {t.key === 'live' && tab !== t.key && <span className="live-dot" />}
              {t.label}
              <span className={`tab-count ${tab === t.key ? 'tab-count-active' : ''}`}>{t.count}</span>
            </button>
          ))}
        </div>
      )}
      <div style={{ position: 'relative', paddingLeft: 28 }}>
        <div style={{ position: 'absolute', left: 9, top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, #00205B, #1E88E5, #00205B)', borderRadius: 1 }} />
        {(() => {
          const grouped = {};
          currentList.forEach(m => {
            const key = m.tournamentId || 'unknown';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(m);
          });
          return Object.entries(grouped).map(([key, ms], idx) => {
            const isLast = idx === Object.keys(grouped).length - 1;
            const name = ms[0]?.tournamentName || 'Unknown Tournament';
            const hasLive = tab === 'live' && ms.some(m => m.status === 'live');
            return (
              <div key={key} style={{ position: 'relative', marginBottom: isLast ? 0 : 20 }}>
                <div style={{ position: 'absolute', left: -27, top: 0, width: 18, height: 18, borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%, #1E3A5F, #00205B 50%, #001840)', boxShadow: '0 2px 6px rgba(0,0,0,0.2), 0 0 0 2px rgba(189,255,0,0.3)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" style={{ position: 'absolute' }}>
                    <path d="M 3 3 Q 9 9 3 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <path d="M 15 3 Q 9 9 15 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  </svg>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {hasLive && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />}
                    <span className="font-display" style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{name}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#00205B', padding: '2px 7px', borderRadius: 10 }}>{ms.length}</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--gray-100)' }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                  {ms.map(m => tab === 'live'
                    ? <LiveMatchCard key={m.id} m={m} players={players} />
                    : tab === 'upcoming'
                    ? <UpcomingMatchCard key={m.id} m={m} players={players} />
                    : <ResultMatchCard key={m.id} m={m} players={players} />)}
                </div>
              </div>
            );
          });
        })()}
        <div style={{ position: 'absolute', left: 1, bottom: -10, width: 18, height: 18, borderRadius: '50%', background: '#43A047', border: '2px solid #fff', boxShadow: '0 0 0 3px rgba(67,160,71,0.2)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="#fff" strokeWidth="2" fill="none" />
            <path d="M12 2c0 5.5-4.5 10-10 10" stroke="#fff" strokeWidth="1.5" fill="none" />
            <path d="M12 22c0-5.5 4.5-10 10-10" stroke="#fff" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function LiveMatchCard({ m, players }) {
  const t1 = resolveTeam(m.player1, players);
  const t2 = resolveTeam(m.player2, players);
  const p1obj = players.find(p => p.id === m.player1);
  const p2obj = players.find(p => p.id === m.player2);
  const sets = m.score?.sets || [];
  const lastIdx = sets.length - 1;

  const g1 = sets.reduce((s, sc) => s + (sc.p1 || 0), 0);
  const g2 = sets.reduce((s, sc) => s + (sc.p2 || 0), 0);

  const renderPlayer = (team, pobj, pKey) => {
    const isLeading = sets.length > 0 && (pKey === 'p1' ? g1 > g2 : g2 > g1);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isLeading ? 'rgba(189,255,0,0.05)' : 'transparent' }}>
        <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: isLeading ? 700 : 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {team.name}
        </span>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {sets.map((sc, i) => {
            const mine = sc[pKey], opp = sc[pKey === 'p1' ? 'p2' : 'p1'];
            const isCurrent = i === lastIdx;
            const won = mine > opp;
            return (
              <span key={i} style={{
                width: isCurrent ? 30 : 24, height: isCurrent ? 30 : 24,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isCurrent ? 15 : 12,
                fontWeight: isCurrent ? 800 : won ? 700 : 400,
                fontFamily: "'Outfit', sans-serif",
                color: isCurrent ? '#E53935' : won ? 'var(--navy)' : 'var(--gray-300)',
                background: isCurrent ? 'rgba(229,57,53,0.1)' : won ? 'var(--gray-100)' : 'transparent',
                borderRadius: 6,
                border: isCurrent ? '1.5px solid rgba(229,57,53,0.3)' : '1.5px solid transparent',
              }}>{mine}</span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1.5px solid #E53935', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: '#E53935', letterSpacing: 1 }}>LIVE</span>
            {sets.length > 0 && <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>Set {sets.length}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
          </div>
        </div>
        <div>
          {renderPlayer(t1, p1obj, 'p1')}
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 16px' }} />
          {renderPlayer(t2, p2obj, 'p2')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function UpcomingMatchCard({ m, players }) {
  const t1 = resolveTeam(m.player1, players);
  const t2 = resolveTeam(m.player2, players);
  const p1obj = players.find(p => p.id === m.player1);
  const p2obj = players.find(p => p.id === m.player2);

  return (
    <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '2px solid #00205B', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>Upcoming</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
            <PlayerAvatar name={t1.name} photo={p1obj?.photo} size={28} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t1.name}
            </span>
          </div>
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 10px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
            <PlayerAvatar name={t2.name} photo={p2obj?.photo} size={28} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t2.name}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--gray-50)', padding: '2px 6px', borderRadius: 5, border: '1px solid var(--gray-200)' }}>
            {m.date && <><Ico d={Icons.calendar} size={8} color='var(--navy)' />
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)' }}>{formatDateIST(m.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span></>}
            {m.time && <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--navy)', fontFamily: 'var(--font-display)' }}>{m.time}</span>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
            {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

function ResultMatchCard({ m, players }) {
  const t1 = resolveTeam(m.player1, players);
  const t2 = resolveTeam(m.player2, players);
  const p1obj = players.find(p => p.id === m.player1);
  const p2obj = players.find(p => p.id === m.player2);
  const sets = m.score?.sets || [];

  const renderPlayer = (team, pobj, pKey) => {
    const isWinner = m.winner === (pKey === 'p1' ? m.player1 : m.player2);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isWinner ? 'rgba(67,160,71,0.08)' : 'transparent', borderLeft: isWinner ? '3px solid #43A047' : '3px solid transparent' }}>
        <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {team.name}
        </span>
        {isWinner && (
          <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#43A047', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5 }}>WON</span>
        )}
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {sets.map((sc, i) => {
            const mine = sc[pKey], opp = sc[pKey === 'p1' ? 'p2' : 'p1'];
            const won = mine > opp;
            return (
              <span key={i} style={{
                width: 24, height: 24,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12,
                fontWeight: won ? 700 : 400,
                fontFamily: "'Outfit', sans-serif",
                color: won ? 'var(--navy)' : 'var(--gray-300)',
                background: won ? 'var(--gray-100)' : 'transparent',
                borderRadius: 6,
              }}>{mine}</span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1.5px solid #43A047', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {sets.length > 0 && <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>{sets.length} Set{sets.length > 1 ? 's' : ''}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
          </div>
        </div>
        <div>
          {renderPlayer(t1, p1obj, 'p1')}
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 10px' }} />
          {renderPlayer(t2, p2obj, 'p2')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--gray-50)', padding: '2px 6px', borderRadius: 5, border: '1px solid var(--gray-200)' }}>
            {m.date && <><Ico d={Icons.calendar} size={8} color='var(--navy)' />
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)' }}>{formatDateIST(m.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span></>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
            {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}
